import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, errMessage } from "@/lib/api";
import { mediaUrl } from "@/lib/media";
import { ROLES, GENRES } from "@/data/options";
import { Header } from "@/components/Header";
import { CallingCard } from "@/components/CallingCard";
import { useAuth } from "@/context/AuthContext";
import { Search, SlidersHorizontal, X, Bookmark } from "lucide-react";
import { toast } from "sonner";

export default function Directory() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [genre, setGenre] = useState("");
  const [remote, setRemote] = useState(false);
  const [openOnly, setOpenOnly] = useState(false);
  const [savedIds, setSavedIds] = useState([]);
  const [savedList, setSavedList] = useState([]);

  useEffect(() => {
    if (user && !user.has_browse_access) navigate("/paywall");
  }, [user, navigate]);

  const loadSaved = () => api.get("/saved").then((r) => {
    setSavedIds(r.data.ids || []);
    setSavedList((r.data.saved || []).slice(0, 10));
  }).catch(() => {});

  useEffect(() => {
    if (user?.has_browse_access) loadSaved();
    // eslint-disable-next-line
  }, [user]);

  const toggleSave = async (uid) => {
    try {
      if (savedIds.includes(uid)) {
        const res = await api.delete(`/saved/${uid}`);
        setSavedIds(res.data.ids || []);
        loadSaved();
        toast("Removed from your playlist");
      } else {
        const res = await api.post(`/saved/${uid}`);
        setSavedIds(res.data.ids || []);
        loadSaved();
        toast.success("Saved to your playlist");
      }
    } catch (e) { toast.error(errMessage(e)); }
  };

  const message = (uid) => navigate(`/messages?to=${uid}`);

  const fetchResults = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (role) params.role = role;
      if (genre) params.genre = genre;
      if (remote) params.remote = true;
      if (openOnly) params.open_only = true;
      const res = await api.get("/profiles", { params });
      setResults(res.data.results || []);
    } catch (e) {
      if (e?.response?.status === 402) navigate("/paywall");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user?.has_browse_access) fetchResults(); /* eslint-disable-next-line */ }, [role, genre, remote, openOnly]);

  const clear = () => { setQ(""); setRole(""); setGenre(""); setRemote(false); setOpenOnly(false); };

  return (
    <div className="min-h-screen bg-ink">
      <Header />
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-24 pb-20">
        <div className="mb-8">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold">Browse the Haus</span>
          <h1 className="font-heading text-4xl md:text-6xl font-bold tracking-tighter uppercase text-cream mt-2">Find your people</h1>
        </div>

        {/* Pinned: your saved writers */}
        {savedList.length > 0 && (
          <div className="mb-10" data-testid="saved-row">
            <div className="flex items-center justify-between mb-3">
              <span className="font-mono text-xs uppercase tracking-[0.18em] text-ash inline-flex items-center gap-2">
                <Bookmark size={13} className="text-gold" /> Your saved writers
              </span>
              <Link to="/saved" data-testid="saved-row-see-all" className="font-mono text-[11px] uppercase tracking-wide text-ash hover:text-gold transition-colors">See all →</Link>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {savedList.map((s) => (
                <button
                  key={s.user_id}
                  data-testid={`saved-chip-${s.user_id}`}
                  onClick={() => navigate(`/profile/${s.user_id}`)}
                  className="shrink-0 flex items-center gap-3 border border-line bg-surface pr-5 hover:border-gold transition-colors group"
                >
                  <span className="w-12 h-12 bg-ink overflow-hidden block">
                    {mediaUrl(s.avatar) && <img src={mediaUrl(s.avatar)} alt={s.name} className="w-full h-full object-cover" />}
                  </span>
                  <span className="text-left">
                    <span className="block text-sm text-cream group-hover:text-gold transition-colors whitespace-nowrap">{s.name}</span>
                    <span className="block font-mono text-[10px] uppercase tracking-wide text-ash whitespace-nowrap">{(s.roles || [])[0] || ""}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Search bar */}
        <div className="flex gap-3 mb-6">
          <div className="flex-1 min-w-0 flex items-center gap-3 border border-line bg-surface px-4">
            <Search size={18} className="text-ash shrink-0" />
            <input
              data-testid="directory-search-input"
              value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchResults()}
              placeholder="Search by name or bio…"
              className="flex-1 min-w-0 bg-transparent py-3.5 text-cream focus:outline-none placeholder:text-ash/50"
            />
          </div>
          <button onClick={fetchResults} data-testid="directory-search-btn" className="shrink-0 bg-gold text-ink font-bold uppercase tracking-widest text-xs px-5 hover:bg-gold-hi transition-colors">Search</button>
        </div>

        {/* Filters */}
        <div className="border border-line bg-surface p-5 mb-10 flex flex-col gap-5">
          <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-ash"><SlidersHorizontal size={14} /> Filters</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <select data-testid="filter-role" value={role} onChange={(e) => setRole(e.target.value)} className="bg-ink border border-line text-cream px-4 py-3 font-mono text-xs uppercase tracking-wide focus:outline-none focus:border-gold">
              <option value="">All roles</option>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select data-testid="filter-genre" value={genre} onChange={(e) => setGenre(e.target.value)} className="bg-ink border border-line text-cream px-4 py-3 font-mono text-xs uppercase tracking-wide focus:outline-none focus:border-gold">
              <option value="">All genres</option>
              {GENRES.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div className="flex flex-wrap items-center gap-5">
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input type="checkbox" data-testid="filter-open" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} className="w-5 h-5 appearance-none border border-ash checked:bg-gold checked:border-gold cursor-pointer" />
              <span className="font-mono text-xs uppercase tracking-wide text-cream">Open to collaborate</span>
            </label>
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input type="checkbox" data-testid="filter-remote" checked={remote} onChange={(e) => setRemote(e.target.checked)} className="w-5 h-5 appearance-none border border-ash checked:bg-gold checked:border-gold cursor-pointer" />
              <span className="font-mono text-xs uppercase tracking-wide text-cream">Remote friendly</span>
            </label>
            {(role || genre || remote || openOnly || q) && (
              <button onClick={() => { clear(); setTimeout(fetchResults, 0); }} data-testid="clear-filters-btn" className="ml-auto inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-wide text-ash hover:text-gold transition-colors"><X size={14} /> Clear</button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="py-20 text-center font-mono text-xs uppercase tracking-[0.3em] text-ash animate-pulse">Searching…</div>
        ) : results.length === 0 ? (
          <div className="py-20 text-center text-ash">No creators match those filters yet. Try widening your search.</div>
        ) : (
          <>
            <p className="font-mono text-xs uppercase tracking-[0.18em] text-ash mb-5" data-testid="results-count">{results.length} creators</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {results.map((p, i) => <CallingCard key={p.user_id} profile={p} index={i} isSelf={p.user_id === user?.user_id} saved={savedIds.includes(p.user_id)} onToggleSave={toggleSave} onMessage={message} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
